import {Injectable} from '@angular/core';
import {catchError, from, map, Observable, switchMap} from "rxjs";
import {environment} from "../../environments/environment";
import {Router} from "@angular/router";
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {IndexedDbService} from "./indexed-db.service";

interface LoginResult {
  username: string;
  role: string;
  accessToken: string;
  refreshToken: string;
  fullname: string;
  id: string;
  apiKey: string;
}

interface OtpResult {
  mfaRequired: boolean;
  mfaToken: string
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly apiUrl = environment.apiUrl;
  private readonly isTokenAuth = environment.isTokenAuth;

  constructor(private router: Router, private http: HttpClient, private dexieDBService: IndexedDbService) {
  }

  async isAuthenticatedUser(): Promise<boolean> {
    const token = await this.getAccesstokenOrRefreshtokenOrApikey('accessToken');

    if (!token) return false; // No token means user is not authenticated

    // Optionally, validate the token (e.g., check expiration)
    return !this.isTokenExpired(token);
  }

  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1])); // Decode JWT payload
      const exp = payload.exp * 1000; // Convert to milliseconds
      return Date.now() > exp;
    } catch (error) {
      return true; // If decoding fails, treat as expired
    }
  }

  getOtpCode(username: string, password: string): Observable<OtpResult> {
    const body = {
      login: username,
      password: password,
    };

    // Utilisation de toPromise() pour transformer l'observable en promesse
    return this.http
      .post<OtpResult>(`${this.apiUrl}/pad/auth`, body)
  }

  login(mfaToken: string, code: string): Promise<Promise<LoginResult> | undefined> {
    const body = {
      mfaToken: mfaToken,
      code: code,
    };

    // Utilisation de toPromise() pour transformer l'observable en promesse
    return this.http
      .post<LoginResult>(`${this.apiUrl}/pad/auth/verify`, body)
      .pipe(
        map(async (loginResult: LoginResult) => {
          await this.setIndexedDB(loginResult); // Attendre que setIndexedDB soit terminé
          localStorage.setItem('login-event', 'login' + Date.now());
          return loginResult;
        }),
        catchError((error) => {
          console.error('Login failed', error);
          throw error;
        })
      )
      .toPromise(); // Convertir l'observable en promesse
  }

  refreshToken() {
    return from(this.getAccesstokenOrRefreshtokenOrApikey('refreshToken')).pipe(
      switchMap((refreshToken) => {
        return this.http.post<LoginResult>(`${this.apiUrl}/pad/auth/refresh`, {refreshToken}).pipe(
          map((loginResult: LoginResult) => {
            loginResult.refreshToken = refreshToken as string;
            this.setIndexedDB(loginResult).then();
            return loginResult;
          }),
          catchError((error) => {
            console.error('Refresh failed', error);
            throw error;
          })
        );
      })
    );
  }


  async getAccesstokenOrRefreshtokenOrApikey(keyName: string): Promise<string | null> {
    try {
      /* if (!this.dexieDBService.isOpen()) {
        console.warn('IndexedDB is not initialized yet. Returning null.');
        return null;
      } */
      let attributeToReturn
      await this.dexieDBService.user.get("ssn").then((user: any) => {
        attributeToReturn = user[keyName];
      })
      return attributeToReturn ? attributeToReturn : null;
    } catch (error) {
      console.error('Error retrieving token from IndexedDB', error);
      return null;
    }
  }


  private createHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Access-Control-Allow-Headers': 'Content-Type',
      'Access-Control-Allow-Methods': 'POST',
      'Access-Control-Allow-Origin': '*'
    });
  }

  private createParams(username: string, password: string): HttpParams {
    return new HttpParams()
      .append('username', username)
      .append('password', password);
  }


  async logout() {
    try {
      this.dexieDBService.getDatasFromIndexDb("user", "ssn").then(result => {
        if (result) {
          let userWithoutToken = result;
          if (userWithoutToken.accessToken) {
            delete userWithoutToken.accessToken;
            delete userWithoutToken.refreshToken;
          } else {
            delete userWithoutToken['api-key']
          }
          this.dexieDBService.deleteAndReplace("user", "ssn", {ssn: "ssn", ...userWithoutToken}).then(() => {
            // Redirect to login page
            this.router.navigate(['/login']).then();
          });
        }
      });


    } catch (error) {
      console.error('Logout failed', error);
    }
  }

  private async deleteIndexedDB(): Promise<void> {
    try {
      this.dexieDBService.delete();
    } catch (error) {
      console.error('Error deleting IndexedDB', error);
      throw error;
    }
  }

  private setIndexedDB(loginResult: LoginResult): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.dexieDBService.getDatasFromIndexDb("user", "ssn").then(result => {
        if (result) {
          this.dexieDBService.deleteAndReplace("user", "ssn", {ssn: "ssn", ...loginResult})
            .then(() => resolve())
            .catch((error) => reject(error));
        } else {
          this.dexieDBService.user.add({ssn: "ssn", ...loginResult}).then(() => resolve())
            .catch((error) => reject(error));
        }
      });
    })
  }

}
